import React, { useEffect, useState } from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ProductIcon from "../assets/icons/ProductIcon";
import CxCenterIcon from "../assets/icons/CxCenterIcon";
import UserRoleIcon from "../assets/icons/UserRoleIcon";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import InstallMobileOutlinedIcon from "@mui/icons-material/InstallMobileOutlined";
import { sidebarItemContainer, sidebarText } from "../styles/styling";
import { AppDispatch, RootState } from "../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import LogoutModal from "./LogoutModal";
import useDetectSession from "../hooks/useDetectSession.hook";
import { logoutUser } from "../redux/slices/authSlice";
import useTabTitle from "../hooks/useTabTitle.hook";
import { menuItems } from "../constants/MenuItems";
import {
  PermissionsByDocKey,
  PermissionsItem,
} from "../interfaces/permissions-interface";
import ResetPassword from "./ResetPasswordModal";
import { getIdleTimeData } from "../redux/slices/idleTimeSlice";
import { useFirebaseAuthToken } from "../hooks/useFirebaseAuthToken.hook";

const malayanIcon = require("../assets/logo/malayan-logo-sm.png");

const drawerWidth = 290;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `55px`,
  [theme.breakpoints.up("sm")]: {
    width: `80px`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => true,
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-between",
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => true,
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export default function AppContainer({ children }: { children: any }) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const idToken = localStorage.getItem("idToken");
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const { idleTime } = useSelector((state: RootState) => state.idleTime);

  useDetectSession();
  useTabTitle();
  useFirebaseAuthToken();

  const [open, setOpen] = useState<boolean>(true);
  const [selected, setSelected] = useState<string>("");
  const [showLogoutPrompt, setShowLogoutPrompt] = useState<boolean>(false);
  const [showResetPassword, setShowResetPassword] = useState<boolean>(false);

  const [userPermissions, setUserPermissions] = useState<PermissionsByDocKey>();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleLogoutClick = () => {
    if (!idToken) {
      navigate("/login");
    }

    const data = { uid: idToken };

    dispatch(logoutUser(data))
      .unwrap()
      .then(() => {
        localStorage.removeItem("idToken");
        navigate("/login");
      });
  };

  const sidebarItem = ({
    tabLabel,
    tabIcon,
    onPressTab = () => {},
  }: {
    tabLabel: string;
    tabIcon: any;
    onPressTab?: any;
  }) => {
    return (
      <ListItem
        key={tabLabel}
        onClick={() => {
          if (onPressTab) {
            onPressTab();
          }
        }}
      >
        <ListItemButton
          sx={[
            sidebarItemContainer,
            open
              ? {
                  justifyContent: "initial",
                }
              : {
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "8px",
                },
          ]}
          selected={tabLabel === selected}
        >
          <ListItemIcon
            sx={[
              {
                minWidth: 0,
                justifyContent: "center",
              },
              open
                ? {
                    mr: 0.6,
                  }
                : {
                    alignSelf: "center",
                    alignItems: "center",
                    justifyContent: "center",
                  },
            ]}
          >
            {tabIcon}
          </ListItemIcon>
          <ListItemText
            primary={tabLabel}
            sx={[
              sidebarText,
              open
                ? {
                    opacity: 1,
                  }
                : {
                    opacity: 0,
                  },
            ]}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
    }
  }, [idToken, navigate]);

  useEffect(() => {
    if (idleTime.minutes) {
      localStorage.setItem("sessionTimeout", idleTime.minutes.toString());
      window.dispatchEvent(new Event("storage"));
    }
  }, [idleTime.minutes]);

  useEffect(() => {
    if (currentUser) {
      dispatch(getIdleTimeData()).unwrap();

      const permissionsByKey: any = {};
      currentUser?.userPermissions?.forEach(
        (rolePermissionItem: PermissionsItem) =>
          (permissionsByKey[rolePermissionItem.permissionKey] = {
            ...rolePermissionItem,
          })
      );
      setUserPermissions(permissionsByKey);
      setShowResetPassword(currentUser?.forcePasswordReset ?? false);
    } else {
      navigate("/login");
    }
  }, [currentUser, dispatch, navigate]);

  useEffect(() => {
    const pathInfo = menuItems.filter(
      (item: { text: string; path: string }) => item.path === location.pathname
    )?.[0] ?? { text: "Users", path: "/dashboard" };
    setSelected(pathInfo.text);
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "111.11vh",
        backgroundColor: "#F7F7F7",
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        sx={{
          width: "100%",
          marginLeft: 0,
          transition: (theme) =>
            theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            height: "70px",
          }}
        >
          {/* Left section: Icon + Title */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={open ? handleDrawerClose : handleDrawerOpen}
              edge="start"
              sx={{ width: "72px", height: "70px", alignItems: "center" }}
            >
              <img
                srcSet={malayanIcon}
                src={malayanIcon}
                alt="Malayan Insurance"
                loading="lazy"
                style={{
                  objectFit: "contain",
                  height: "40px",
                  width: "40px",
                }}
              />
            </IconButton>
            <Typography
              variant="h4"
              noWrap
              component="div"
              sx={{
                fontFamily: "Open Sans",
                fontSize: "18px",
                fontWeight: 700,
                lineHeight: "24.51px",
                marginLeft: 1,
              }}
            >
              Malayan Insurance
            </Typography>
          </Box>

          {/* Right section: User info */}
          {currentUser ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Open Sans",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "19.07px",
                  textAlign: "right",
                }}
              >
                Hello, {currentUser?.fullname}
              </Typography>
              <IconButton color="inherit" aria-label="open menu" edge="end">
                <ExpandMoreOutlinedIcon style={{ fontSize: "20px" }} />
              </IconButton>
            </Box>
          ) : (
            <Box />
          )}
        </Toolbar>
      </AppBar>
      <Box style={{ width: "100%", overflowY: "auto" }}>
        <Drawer
          variant="permanent"
          open={open}
          PaperProps={{
            sx: {
              backgroundColor: "#FFFFFF",
              color: "#000",
            },
          }}
        >
          <DrawerHeader />
          <Divider />
          <List>
            {userPermissions?.productsList?.permissions?.view &&
              sidebarItem({
                tabLabel: "Product Management",
                tabIcon: <ProductIcon />,
                onPressTab: () => navigate("/products"),
              })}
            {userPermissions?.notification?.permissions?.view &&
              sidebarItem({
                tabLabel: "Notification Management",
                tabIcon: <NotificationsNoneOutlinedIcon />,
                onPressTab: () => navigate("/notification"),
              })}
            {userPermissions?.contactSupport?.permissions?.view &&
              sidebarItem({
                tabLabel: "CX Center Management",
                tabIcon: <CxCenterIcon />,
                onPressTab: () => navigate("/support"),
              })}
            {userPermissions?.helpCenter?.permissions?.view &&
              sidebarItem({
                tabLabel: "Help Center Management",
                tabIcon: <HelpOutlineIcon />,
                onPressTab: () => navigate("/help-center"),
              })}
            {userPermissions?.termsAndConditions?.permissions?.view &&
              sidebarItem({
                tabLabel: "Terms and Condition",
                tabIcon: <DescriptionOutlinedIcon />,
                onPressTab: () => navigate("/terms-and-conditions"),
              })}
            {userPermissions?.auditTrail?.permissions?.view &&
              sidebarItem({
                tabLabel: "Audit Trail",
                tabIcon: <HistoryEduIcon />,
                onPressTab: () => navigate("/audit-trail"),
              })}
            {userPermissions?.mobileAppIdleTime?.permissions?.view &&
              sidebarItem({
                tabLabel: "Idle Time",
                tabIcon: <HourglassTopIcon />,
                onPressTab: () => navigate("/idle-time"),
              })}
          </List>
          <Divider />
          {(userPermissions?.user?.permissions?.view ||
            userPermissions?.userRole?.permissions?.view) &&
            open && (
              <Box sx={[sidebarItemContainer, { padding: "8px" }]}>
                <Typography
                  sx={[sidebarText, { paddingLeft: "20px", fontSize: "18px" }]}
                >
                  User Management
                </Typography>
              </Box>
            )}
          <List>
            {userPermissions?.user?.permissions?.view &&
              sidebarItem({
                tabLabel: "Users",
                tabIcon: <PersonOutlineOutlinedIcon />,
                onPressTab: () => navigate("/dashboard"),
              })}
            {userPermissions?.userRole?.permissions?.view &&
              sidebarItem({
                tabLabel: "Manage User Roles",
                tabIcon: <UserRoleIcon />,
                onPressTab: () => navigate("/manage-user-role"),
              })}
          </List>
          <Divider />
          {(userPermissions?.user?.permissions?.view ||
            userPermissions?.userRole?.permissions?.view) &&
            open && (
              <Box sx={[sidebarItemContainer, { padding: "8px" }]}>
                <Typography
                  sx={[sidebarText, { paddingLeft: "20px", fontSize: "18px" }]}
                >
                  Mobile App Alerts
                </Typography>
              </Box>
            )}
          <List>
            {sidebarItem({
              tabLabel: "App Updates",
              tabIcon: <InstallMobileOutlinedIcon />,
              onPressTab: () => navigate("/app-updates"),
            })}
          </List>
          <Box p={1} mt="auto" mb={2}>
            {open ? (
              <Button
                variant="contained"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyItems: "center",
                  padding: "10px",
                  gap: "10px",
                  backgroundColor: "#2FB9C2",
                  width: "260px",
                  height: "40px",
                  borderRadius: "20px",
                }}
                onClick={() => setShowLogoutPrompt(true)}
              >
                <LogoutOutlinedIcon />
                <Typography>Log out</Typography>
              </Button>
            ) : (
              <Button
                variant="contained"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyItems: "center",
                  backgroundColor: "#2FB9C2",
                  width: "40px",
                  height: "40px",
                  borderRadius: "20px",
                }}
                onClick={() => setShowLogoutPrompt(true)}
              >
                <LogoutOutlinedIcon />
              </Button>
            )}
          </Box>
        </Drawer>
        <Box
          sx={{
            marginLeft: open ? "290px" : "120px",
            width: "80%",
            flexGrow: 1,
            p: 3,
            alignItems: "center",
            justifyContent: "center",
            overflowY: "auto",
            backgroundColor: "#F7F7F7",
          }}
        >
          <DrawerHeader />
          {children}
          <LogoutModal
            showLogoutPrompt={showLogoutPrompt}
            onClose={() => setShowLogoutPrompt(false)}
            onConfirm={() => {
              handleLogoutClick();
            }}
          />
          <ResetPassword
            open={showResetPassword}
            handleClose={() => {
              setShowLogoutPrompt(false);
              handleLogoutClick();
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
